// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { createElement, cloneElement, findDOMNode, Component, Fragment } from '../../utils';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CLS_PREFIX } from '../../constants';
import PopperJs from 'popper.js';
import Layer from '../Layer';

/**
 * `Popper` component.
 */
class Popper extends Component {
    constructor(props) {
        super(props);

        this.state = {
            popperStyle: null,
            arrowStyle: null,
            actualPlacement: props.placement,
        };
    }

    componentDidMount() {
        this.updatePopper(this.props);
    }

    componentWillReceiveProps(nextProps) {
        setTimeout(() => {
            this.updatePopper(nextProps);
        }, 0);
    }

    componentWillUnmount() {
        if (this.popper) {
            this.popper.destroy();
        }
    }

    popper = null;

    contentRef = null;

    updatePopper(props) {
        if (this.popper) {
            this.popper.destroy();
        }

        if (!this.contentRef || !props.show) {
            return;
        }

        const popperOpts = {
            placement: props.placement,
            modifiers: {
                applyStyle: {
                    enabled: false,
                },
                applyReactStyle: {
                    enabled: true,
                    fn: this.extractStyles,
                    order: 900,
                },
                hide: {
                    enabled: false,
                },
                offset: {
                    enabled: true,
                    offset: props.offset,
                },
                flip: {
                    enabled: true,
                    padding: 0,
                    behavior: props.behavior,
                },
                preventOverflow: {
                    enabled: true,
                    boundariesElement: 'viewport',
                },
                arrow: props.arrow && {
                    enabled: true,
                    element: this.arrowRef,
                },
            },
        };

        this.popper = new PopperJs(findDOMNode(this), this.contentRef, popperOpts);
    }

    extractStyles = state => {
        if (!state) {
            return;
        }

        const { arrow, onUpdate } = this.props;

        const { left, top } = state.offsets.popper;
        const data = {
            popperStyle: {
                position: state.offsets.popper.position || null,
                left: left || null,
                top: top || null,
            },
            actualPlacement: state.placement,
            arrowStyle: arrow && {
                position: state.offsets.arrow.position || null,
                left: state.offsets.arrow.left || null,
                top: state.offsets.arrow.top || null,
            },
        };

        if (arrow && (data.arrowStyle.left === null && data.arrowStyle.top === null)) {
            data.arrowStyle.top = 0;
        }

        this.setState(data);

        if (onUpdate) {
            onUpdate(data);
        }
    };

    render() {
        const {
            baseClassName,
            className,
            children,
            target,
            arrow,
            placement,
            behavior,
            offset,
            onUpdate,
            zIndex,
            show,
            ...props
        } = this.props;
        const { popperStyle, arrowStyle, actualPlacement } = this.state;

        const arrowElement = arrow && cloneElement(arrow, {
            ref: ref => (this.arrowRef = ref),
            style: arrowStyle,
        });

        return (
            <Fragment>
                {target}
                {show ? (
                    <Layer level={zIndex}>
                        <div
                            ref={ref => (this.contentRef = ref)}
                            className={classNames(
                                baseClassName,
                                className,
                                `${baseClassName}--placement-${actualPlacement}`,
                            )}
                            style={{
                                position: 'absolute',
                                ...popperStyle,
                            }}
                            {...props}
                        >
                            {children}
                            {arrowElement}
                        </div>
                    </Layer>
                ) : null}
            </Fragment>
        );
    }
}

Popper.propTypes = {
    /**
     * Whether show popper content.
     */
    show: PropTypes.bool,
    /**
     * Content of the `Popper`.
     */
    children: PropTypes.node,
    /**
     * Target for popper.
     */
    target: PropTypes.any.isRequired,
    /**
     * Arrow element of popper
     */
    arrow: PropTypes.element,
    /**
     * Placement for popper.
     */
    placement: PropTypes.oneOf([
        'auto',
        'auto-start',
        'auto-end',
        'auto-top',
        'auto-right',
        'auto-bottom',
        'auto-left',
        'top',
        'top-start',
        'top-end',
        'right',
        'right-start',
        'right-end',
        'bottom',
        'bottom-start',
        'bottom-end',
        'left',
        'left-start',
        'left-end',
    ]),
    /**
     * The behavior used to change the popper's placement.
     */
    behavior: PropTypes.oneOfType([
        PropTypes.oneOf(['flip', 'clockwise', 'counterclockwise']),
        PropTypes.arrayOf(PropTypes.string),
    ]),
    /**
     * Shift your popper on both axis.
     */
    offset: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    /**
     * Update callback function
     */
    onUpdate: PropTypes.func,
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * @ignore
     */
    baseClassName: PropTypes.string,
    /**
     * zIndex value.
     */
    zIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

Popper.defaultProps = {
    show: true,
    children: null,
    arrow: null,
    placement: 'bottom-start',
    behavior: 'flip',
    offset: '0, 0',
    onUpdate: null,
    className: null,
    baseClassName: `${CLS_PREFIX}popper`,
    zIndex: null,
};

export default Popper;



// WEBPACK FOOTER //
// ./components/Popper/Popper.js